import { functions } from '@/shared/services/firebase'
import axios from '@hw-core/libs/axios'
import { geohashForLocation } from 'geofire-common'
import { 
  fetchItems, createItem, partialUpdateItem, deleteItem, buildSortOption,
  buildEqualCondition, buildGreaterOrEqualCondition, buildSmallerOrEqualCondition, buildLimit } from '@/shared/services/collection'
import { DB_NAMES } from '@/shared/constants/collections'

const buildFetchAreasConditions = (accountId, startcode, endcode) => [
  buildEqualCondition('accountId', accountId),
  buildGreaterOrEqualCondition('name', startcode),
  buildSmallerOrEqualCondition('name', endcode)
]

export default {
  namespaced: true,
  state: {},
  getters: {},
  mutations: {},
  actions: {
    async fetchAreas(ctx, { searchQuery, accountId, userAreas }) {
      try {
        let conditions = [buildEqualCondition('accountId', accountId)]

        if (searchQuery && searchQuery.length > 0) {
          const strlength = searchQuery.length
          const strFrontCode = searchQuery.slice(0, strlength - 1)
          const strEndCode = searchQuery.slice(strlength - 1, strlength)
          const startcode = searchQuery
          const endcode = strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1)
          
          conditions = buildFetchAreasConditions(accountId, startcode.toLowerCase(), endcode.toLowerCase())
        }

        const areas = await fetchItems(DB_NAMES.AREAS, conditions, userAreas)

        const finalAreas = areas.map(area => {
          const {streetAddress, city, postcode, state} = area.location.address
          return {
            ...area, streetAddress, city, postcode, state,
          }
        }).filter(area => area.status === undefined || area.status !== 'deleted')

        return userAreas === null
          ? finalAreas
          : finalAreas.filter(area => userAreas.includes(area.id))
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async createArea(ctx, { formattedArea, accountId }) {
      const emails = formattedArea.emails.map(email => email.toLowerCase())
      const confirmationEmails = formattedArea.confirmation_emails.map(email => email.toLowerCase())
      const { lat, lon } = formattedArea.location.gps
      const geohash = geohashForLocation([lat, lon])
      const location = { ...formattedArea.location, gps: { lat, lon, geohash } }
      const data = {
        name: formattedArea.name?.toLowerCase(),
        emails,
        location,
        accountId,
        contract: formattedArea.contract,
        category: formattedArea.category,
        confirmation_emails: confirmationEmails,
        requirements: formattedArea.requirements,
      }

      try {
        await createItem(DB_NAMES.AREAS, data)
        return Promise.resolve()
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async editArea(ctx, { formattedArea }) {
      const emails = formattedArea.emails.map(email => email.toLowerCase())
      const confirmationEmails = formattedArea.confirmation_emails.map(email => email.toLowerCase())
      const { lat, lon } = formattedArea.location.gps
      const geohash = geohashForLocation([lat, lon])
      const location = { ...formattedArea.location, gps: { lat, lon, geohash } }

      try {
        await partialUpdateItem(DB_NAMES.AREAS, formattedArea.id, {
          ...formattedArea,
          name: formattedArea.name?.toLowerCase(),
          emails,
          location,
          confirmation_emails: confirmationEmails,
        })
        return Promise.resolve()
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async deleteArea(ctx, { id }) {
      try {
        // Check if area has at least one shift
        const conditions = [buildEqualCondition('areaId', id)]
        
        const shifts = await fetchItems(DB_NAMES.SHIFTS, conditions, null, buildLimit(1))
        if(shifts.length > 0) {
          return Promise.reject(new Error('Area has at least one shift'))
        }
        
        await deleteItem(DB_NAMES.AREAS, id)
        return Promise.resolve()
      } catch (error) {
        return Promise.reject(error)
      }
    },
    fetchLocationLatLon(ctx, { address }) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${functions.url}/locationToLatLon`, { address })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    async fetchContracts(ctx, { accountId }) {
      try {
        const conditions = [buildEqualCondition('accountId', accountId)]
        const pagination = buildLimit(100)
        const sortOptions = buildSortOption('created', 'desc')

        const contracts = await fetchItems(DB_NAMES.CONTRACTS, conditions, sortOptions, pagination)

        return contracts.filter(contract => contract.type !== 'campaign')
      } catch (error) {
        return Promise.reject(error)
      }
    },
  },
}
